import { Typography, Card, Box, useMediaQuery, Theme } from "@mui/material";
import { useTranslation } from "react-i18next";
import AnimatedBlock from "../../../components/Common/AnimatedBlock";
import AuditTimeline, { AuditItem, AuditKind } from "../../../components/Audit/AuditTimeline";

type AuditEntry = { id: number; at: string; action: string; text: string; by?: { email?: string; name?: string; userId?: string; system?: string } | null };

// The worker writes "FROM -> TO: detail" for a status change.
const TRANSITION = /^([A-Z_]+) -> ([A-Z_]+):\s*/;

// What an order event is about, read from its line (the API records most as NOTE).
const KIND_RULES: [RegExp, AuditKind][] = [
  [/^Created by/, "created"],
  [/^Quote /i, "quote"],
  [/\bKYT\b/i, "kyt"],
  [/sweep|gas top-up/i, "sweep"],
  [/refund/i, "refund"],
  [/autoConvert|conversion|sent to Coinhouse/i, "conversion"],
  [/transaction seen|payment|received|confirmed/i, "payment"],
];

const toItem = (entry: AuditEntry): AuditItem => {
  const by = entry.by;
  const isSystem = !by || Boolean(by.system);
  const actor = !by ? "System" : by.system ? (by.system === "payer" ? "Payer" : "System") : by.name || by.email || by.userId || "—";
  const text = entry.text || "";
  const transition = text.match(TRANSITION);
  if (transition) return { id: entry.id, at: entry.at, kind: "status", actor, isSystem, from: transition[1], to: transition[2], text: text.slice(transition[0].length) };
  const kind = KIND_RULES.find(([rule]) => rule.test(text))?.[1] || "other";
  return { id: entry.id, at: entry.at, kind, actor, isSystem, text };
};

// The order's audit trail, most recent first (the API returns it oldest first).
function InfoAuditTab({ entries }: { entries: AuditEntry[] }) {
  const { t }: { t: any } = useTranslation();
  const isMobile = useMediaQuery<Theme>((theme) => theme.breakpoints.down("sm"));

  if (entries.length === 0) return null;

  return (
    <Box>
      {!isMobile && (
        <Box pb={2}>
          <Typography variant="h6">{t("Audit trail")}</Typography>
          <Typography variant="body2" color="text.secondary">{t("Every event recorded on this transaction.")}</Typography>
        </Box>
      )}
      <AnimatedBlock>
        <Card sx={{ px: { xs: 2, sm: 3 }, py: 2.5 }}>
          <AuditTimeline items={[...entries].reverse().map(toItem)} />
        </Card>
      </AnimatedBlock>
    </Box>
  );
}

export default InfoAuditTab;
